import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// origin/main literals: packages/lib/src/ask-helix.ts
// (ASK_HELIX_PRICING_REALIZATION_DATA_LAKE_CONTEXT_KIND / _SCHEMA_VERSION,
// ASK_HELIX_PRICING_DISCOUNT_POLICY_AUTHORITY_CONTEXT_KIND / _SCHEMA_VERSION,
// ASK_HELIX_PRICING_DISCOUNT_TRANSACTION_CONTEXT_KIND / _SCHEMA_VERSION),
// re-exported through pricing-realization.types.ts and queried by
// pricing-realization.data-lake-source.ts.
//
// Unlike the single-artifact modules, pricing-realization resolves its evidence
// from THREE distinct governed artifact identities. All three confirmed live on
// tenant helix_retail 2026-08-08 (read-only):
//   - governed_snapshot               2 APPROVED rows (latest approvedAt 2026-07-05)
//   - discount_policy_authority_matrix 1 APPROVED row  (2026-06-26)
//   - discount_transaction_snapshot    1 APPROVED row  (2026-06-26)
// The freshest governed snapshot's own evidenceRefs carry
// approved_pricing_realization_snapshot at confidence=high, and its top-level
// executionCostUsd member is populated — which is exactly the condition
// pricing-realization.data-lake-source.ts uses to publish
// approved_pricing_execution_cost (`state.executionCostUsd !== null`).
//
// SCOPE NOTE (deliberate, disclosed — do not "complete" this contract without
// re-reading the live data): the manifest names two further evidence keys,
// `approved_pricing_structure_mix_snapshot` and
// `approved_spot_to_contract_intervention_exchange`. Both are published by
// buildPricingStructureEvidenceRefs() ONLY when the optional
// pricingStructureEvidence / spotToContractInterventionExchange payload members
// are present on the snapshot, and BOTH are absent from every live approved
// artifact on this tenant (the freshest snapshot's evidenceRefs carry neither).
// They are also fixture-labeled in the engine's own ref descriptions. Registering
// them against this contextKind would make the instrument count the snapshot's
// APPROVED rows and report PRESENT for sub-payloads that do not exist — a false
// green of exactly the kind this substrate exists to prevent. They stay
// CONTRACT_ABSENT until real data lands.
export const PRICING_REALIZATION_EVIDENCE_KEYS = {
  realizationSnapshot: 'approved_pricing_realization_snapshot',
  executionCost: 'approved_pricing_execution_cost',
  discountPolicyAuthorityMatrix: 'approved_discount_policy_authority_matrix',
  discountTransactionSnapshot: 'approved_discount_transaction_snapshot',
} as const;

export const PRICING_REALIZATION_CONTEXT_KINDS = {
  governedSnapshot: 'decision_agent:pricing_realization:governed_snapshot',
  discountPolicyAuthorityMatrix:
    'decision_agent:pricing_realization:discount_policy_authority_matrix',
  discountTransactionSnapshot:
    'decision_agent:pricing_realization:discount_transaction_snapshot',
} as const;

export const PRICING_REALIZATION_SCHEMA_VERSIONS = {
  governedSnapshot: 'pricing_realization_governed_snapshot_v1',
  discountPolicyAuthorityMatrix: 'pricing_discount_policy_authority_matrix_v1',
  discountTransactionSnapshot: 'pricing_discount_transaction_snapshot_v1',
} as const;

const NO_PRICING_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

// Every field mirrors pricing-realization.manifest.ts requiredState for the same
// stateKey — including the split missingBehavior: the two snapshot-backed keys
// block, the two discount-artifact keys degrade.
const pricingSnapshotEntry = (
  evidenceKey:
    | typeof PRICING_REALIZATION_EVIDENCE_KEYS.realizationSnapshot
    | typeof PRICING_REALIZATION_EVIDENCE_KEYS.executionCost,
  description: string,
) =>
  defineModuleEvidenceEntry(evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.modeled,
    contextKind: PRICING_REALIZATION_CONTEXT_KINDS.governedSnapshot,
    schemaVersion: PRICING_REALIZATION_SCHEMA_VERSIONS.governedSnapshot,
    bindingPlaceholders: NO_PRICING_BINDING_PLACEHOLDERS,
    domain: 'commercial',
    freshnessCadence: 'monthly',
    requiredFor: ['ask_helix', 'monitoring', 'decision_pack'],
    missingBehavior: 'block',
    description,
  });

export const PRICING_REALIZATION_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'pricing-realization',
  {
    realization_snapshot: pricingSnapshotEntry(
      PRICING_REALIZATION_EVIDENCE_KEYS.realizationSnapshot,
      'Approved pricing-realization governed snapshot; carries quoted, invoiced, and net revenue ' +
        'with the AGP flow-through the realization and price-volume-mix reads resolve from.',
    ),
    execution_cost: pricingSnapshotEntry(
      PRICING_REALIZATION_EVIDENCE_KEYS.executionCost,
      'Approved pricing execution cost carried on the same governed snapshot (executionCostUsd); ' +
        'the cost side that keeps realization gains from being reported gross of what they cost.',
    ),
    discount_policy_authority_matrix: defineModuleEvidenceEntry(
      PRICING_REALIZATION_EVIDENCE_KEYS.discountPolicyAuthorityMatrix,
      {
        sourceClass: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
        contextKind: PRICING_REALIZATION_CONTEXT_KINDS.discountPolicyAuthorityMatrix,
        schemaVersion: PRICING_REALIZATION_SCHEMA_VERSIONS.discountPolicyAuthorityMatrix,
        bindingPlaceholders: NO_PRICING_BINDING_PLACEHOLDERS,
        domain: 'commercial',
        freshnessCadence: 'monthly',
        requiredFor: ['monitoring', 'ask_helix'],
        missingBehavior: 'degrade',
        description:
          'Approved discount policy authority matrix published as its own governed artifact; ' +
          'defines who may approve which discount depth, so leakage can be split into ' +
          'within-policy and outside-policy.',
      },
    ),
    discount_transaction_snapshot: defineModuleEvidenceEntry(
      PRICING_REALIZATION_EVIDENCE_KEYS.discountTransactionSnapshot,
      {
        sourceClass: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
        contextKind: PRICING_REALIZATION_CONTEXT_KINDS.discountTransactionSnapshot,
        schemaVersion: PRICING_REALIZATION_SCHEMA_VERSIONS.discountTransactionSnapshot,
        bindingPlaceholders: NO_PRICING_BINDING_PLACEHOLDERS,
        domain: 'commercial',
        freshnessCadence: 'monthly',
        requiredFor: ['monitoring', 'ask_helix'],
        missingBehavior: 'degrade',
        description:
          'Approved discount transaction snapshot published as its own governed artifact; the ' +
          'transaction population the discount-leakage diagnostic measures against policy.',
      },
    ),
  },
  {
    evidenceKeys: Object.values(PRICING_REALIZATION_EVIDENCE_KEYS),
    sourceClasses: [
      DECISION_AGENT_SOURCE_CLASSES.modeled,
      DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
    ],
    contextKinds: Object.values(PRICING_REALIZATION_CONTEXT_KINDS),
    schemaVersions: Object.values(PRICING_REALIZATION_SCHEMA_VERSIONS),
    stateKeys: Object.values(PRICING_REALIZATION_EVIDENCE_KEYS),
    bindingPlaceholders: NO_PRICING_BINDING_PLACEHOLDERS,
  },
);

export type PricingRealizationModuleEvidenceContract =
  typeof PRICING_REALIZATION_MODULE_EVIDENCE_CONTRACT;
